import React, {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";

const THEME_STORAGE_KEY = "bob-public-theme";
const THEME_MEDIA_QUERY = "(prefers-color-scheme: dark)";
const THEME_SYSTEM = "system";
const THEME_LIGHT = "light";
const THEME_DARK = "dark";

const ThemeContext = createContext(null);

const getSystemTheme = () => {
  if (typeof window === "undefined") return THEME_LIGHT;
  return window.matchMedia(THEME_MEDIA_QUERY).matches ? THEME_DARK : THEME_LIGHT;
};

const normalizeStoredTheme = (value) => {
  if (value === THEME_LIGHT || value === THEME_DARK) return value;
  if (value === THEME_SYSTEM) return getSystemTheme();
  return THEME_LIGHT;
};

const getStoredTheme = () => {
  if (typeof window === "undefined") return THEME_LIGHT;
  const value = window.localStorage.getItem(THEME_STORAGE_KEY);
  return normalizeStoredTheme(value);
};

const applyDocumentTheme = (theme) => {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const isDark = theme === THEME_DARK;
  root.classList.toggle("dark", isDark);
  root.dataset.theme = theme;
  root.style.colorScheme = isDark ? "dark" : "light";
};

export function ThemeProvider({ children }) {
  const [preference, setPreference] = useState(() => getStoredTheme());
  const [theme, setTheme] = useState(() => getStoredTheme());

  useLayoutEffect(() => {
    applyDocumentTheme(theme);
  }, [theme]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === THEME_SYSTEM) {
      const resolved = normalizeStoredTheme(stored);
      window.localStorage.setItem(THEME_STORAGE_KEY, resolved);
      setPreference(resolved);
      setTheme(resolved);
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleStorage = (event) => {
      if (event.key !== THEME_STORAGE_KEY) return;
      const nextPreference = getStoredTheme();
      setPreference(nextPreference);
      setTheme(nextPreference);
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const setThemePreference = (nextTheme) => {
    if (typeof window === "undefined") return;
    if (nextTheme !== THEME_LIGHT && nextTheme !== THEME_DARK) return;
    window.localStorage.setItem(THEME_STORAGE_KEY, nextTheme);
    setPreference(nextTheme);
    setTheme(nextTheme);
  };

  const toggleTheme = () => {
    setThemePreference(theme === THEME_DARK ? THEME_LIGHT : THEME_DARK);
  };

  const value = useMemo(
    () => ({
      theme,
      preference,
      isDark: theme === THEME_DARK,
      setThemePreference,
      toggleTheme,
    }),
    [preference, theme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const value = useContext(ThemeContext);
  if (!value) {
    throw new Error("useTheme must be used inside ThemeProvider");
  }
  return value;
}
